import React, { FC, useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { animated, useSpring } from 'react-spring'

import { NotificationActionType } from '@app/store/notifications/types'
import { removeNotification } from '@app/store/notifications'

const AUTOCLOSE_TIME = 10000

interface NotificationMessageProps {
  actionType: NotificationActionType
  className?: string
  id: string
  autoCloseTime?: number
}

const ActionLink = styled.span`
  color: ${({ theme }) => theme.global.colors.sunglow};
  font-weight: 500;
  cursor: pointer;
  display: inline-block;
  margin-left: 4rem;
  flex-shrink: 0;

  :hover {
    opacity: 0.8;
  }

  @media ${({ theme }) => theme.device.mobileL} {
    margin-left: 2rem;
  }
`

const MessageWrapper = styled.div`
  font-size: 1.5rem;
  line-height: 2.9rem;
  display: flex;
  align-items: center;
  width: 100%;
  text-align: center;
  padding: 1.2rem 2rem;
  background-color: ${({ theme }) => theme.global.colors.elemyGreen};
  justify-content: center;

  @media ${({ theme }) => theme.device.mobileL} {
    justify-content: space-between;
  }
`

const Message = styled.span`
  color: ${({ theme }) => theme.global.colors.white};
  text-align: left;
`
const AnimatedWrapper = animated(MessageWrapper)

export const NotificationsMessage: FC<NotificationMessageProps> = ({
  className,
  actionType,
  id,
  children,
  autoCloseTime
}) => {
  const dispatch = useDispatch()

  useEffect(() => {
    const autoCloseTimeout = setTimeout(() => dispatch(removeNotification(id)), autoCloseTime || AUTOCLOSE_TIME)
    return () => clearTimeout(autoCloseTimeout)
  }, [dispatch, id, autoCloseTime])

  const handleClose = useCallback(() => {
    dispatch(removeNotification(id))
  }, [dispatch, id])

  const handleReload = useCallback(() => window.location.reload(), [])

  const ACTION_LINKS = useMemo(
    () => ({
      [NotificationActionType.Close]: <ActionLink onClick={handleClose}>Close</ActionLink>,
      [NotificationActionType.Reload]: <ActionLink onClick={handleReload}>Reload Page</ActionLink>
    }),
    [handleClose, handleReload]
  )

  const fadeIn = useSpring({ opacity: 1, y: 0, from: { opacity: 0, y: 10 }, leave: { opacity: 0, y: 10 } })

  return (
    <AnimatedWrapper style={fadeIn} className={className}>
      <Message>{children}</Message>
      {ACTION_LINKS[actionType]}
    </AnimatedWrapper>
  )
}
